<template>
    <template v-if="item">
        <!-- Gallery -->
        <div class="container mx-auto px-4">
            <div class="relative">
                <Button @click="$router.back()" class="absolute top-2 md:top-4 left-2 md:left-4" severity="default" size="md" icon="icon-left" iconPos="left">
                    {{ $t('Back') }}
                </Button>
                <Gallery :item="item" />
            </div>
            <p v-if="item.gallery?.length || item.property?.gallery?.length" class="text-gray-600 text-xs mt-2">{{ $t('The photos are illustrative and not binding. Dimensions, room layout and equipment may differ from those shown. Please refer to the accommodation description.') }}</p>
        </div>

        <!-- Sticky navigation -->

        <Nav :items="sections" />
        <!-- Content -->
        <div class="container mx-auto px-4 mt-8">
            <div class="flex gap-10">
                <!-- Main column -->
                <div class="flex-1 space-y-8">
                    <ProductDetails id="details" :item="item" />

                    <div class="divide-y divide-gray-300 space-y-8 *:pb-8">
                        <Properties v-if="hasProperties" id="offer" :item="item" />

                        <Description v-if="item.description" id="description" :item="item" />

                        <Availability id="availability" :item="item" />

                        <AdditionalCosts v-if="hasAdditionalCosts" id="additional-costs" :item="item" />

                        <Location v-if="hasProperty" id="location" :item="item" />

                        <Reviews v-if="hasReviews" id="reviews" :reviews="item.reviews" />
                    </div>
                </div>

                <!-- Sidebar -->
                <div class="w-[340px] shrink-0 xl:block hidden">
                    <div class="sticky top-20 z-10">
                        <Booking :item="item" />
                    </div>
                </div>
            </div>

            <RelatedProducts v-if="relatedProducts?.length" id="related-products" :related-products="relatedProducts" />
        </div>
    </template>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useProductStore } from '@/stores/product'
import { useProductPriceAvailabilityStore } from '@/stores/productPriceAvailability'
import ProductDetails from './_partials/ProductDetails.vue'
import Nav from './_partials/Nav.vue'
import Properties from './_partials/Properties.vue'
import Gallery from './_partials/Gallery.vue'
import Description from './_partials/Description.vue'
import Availability from './_partials/Availability.vue'
import AdditionalCosts from './_partials/AdditionalCosts.vue'
import Location from './_partials/Location.vue'
import Reviews from './_partials/Reviews.vue'
import RelatedProducts from './_partials/RelatedProducts.vue'
import Booking from './_partials/Booking.vue'

const storeProduct = useProductStore()
const storeProductPriceAvailability = useProductPriceAvailabilityStore()

const { item, relatedProducts } = storeToRefs(storeProduct)

await storeProduct.initialize()
await storeProductPriceAvailability.initialize()

/* -----------------------------
 * Section visibility flags
 * ----------------------------- */
const hasProperties = computed(() => item.value?.properties && Object.keys(item.value.properties).length > 0)

const hasAdditionalCosts = computed(() => item.value?.additional_costs?.length || item.value?.additionalCosts?.length)

const hasReviews = computed(() => item.value?.reviews?.length || item.value?.rating)

const hasProperty = computed(() => item.value?.property)

/* -----------------------------
 * Navigation sections
 * ----------------------------- */
const sections = computed(() => [
    {
        id: 'details',
        label: 'Product details',
        show: true,
    },
    {
        id: 'offer',
        label: 'Facility offer',
        show: hasProperties.value,
    },
    {
        id: 'description',
        label: 'Description',
        show: !!item.value?.description,
    },
    {
        id: 'availability',
        label: 'Availability',
        show: true,
    },
    {
        id: 'additional-costs',
        label: 'Additional costs',
        show: hasAdditionalCosts.value,
    },
    {
        id: 'location',
        label: 'Location',
        show: hasProperty.value,
    },
    {
        id: 'reviews',
        label: 'Reviews',
        show: hasReviews.value,
    },
    {
        id: 'related-products',
        label: 'Related Products',
        show: relatedProducts.value?.length,
    },
])
</script>
